/* eslint-disable complexity */
const HOURS_INDEX = 0;
const MINUTES_INDEX = 1;
const SECONDS_INDEX = 2;
const MIN_TIME_PARTS = 3;
const HOURS_IN_DAY = 24;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;

export type CheckinCountdownStatus = 'hidden' | 'warning' | 'expired';

export interface CheckinCountdownState {
  status: CheckinCountdownStatus;
  endTime: Date | null;
}

const HIDDEN_STATE: CheckinCountdownState = { status: 'hidden', endTime: null };

/**
 * Derives the check-in countdown popup state from the "HH:MM:SS" remaining time
 * and the "warningTime" (minutes) validation rule.
 * - >= 24 hours remaining: no popup
 * - 0 hours remaining and minutes below the warning threshold: show live countdown
 * - 00:00:00 remaining: session expired
 */
export const getCheckinCountdownState = (
  remainingTimeToCheckin: string | null | undefined,
  warningTimeMinutes: string | null | undefined,
): CheckinCountdownState => {
  const parts = remainingTimeToCheckin ? remainingTimeToCheckin.split(':') : [];
  if (parts.length < MIN_TIME_PARTS) {
    return HIDDEN_STATE;
  }

  const hours = Number(parts[HOURS_INDEX]);
  const minutes = Number(parts[MINUTES_INDEX]);
  const seconds = Number(parts[SECONDS_INDEX]);
  const warningTime = Number(warningTimeMinutes);

  if ([hours, minutes, seconds].some((part) => Number.isNaN(part)) || hours >= HOURS_IN_DAY) {
    return HIDDEN_STATE;
  }

  if (hours === 0 && minutes === 0 && seconds === 0) {
    return { status: 'expired', endTime: null };
  }

  if (hours === 0 && !Number.isNaN(warningTime) && minutes < warningTime) {
    const totalSeconds = hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + seconds;
    const endTime = new Date();
    endTime.setSeconds(endTime.getSeconds() + totalSeconds);
    return { status: 'warning', endTime };
  }

  return HIDDEN_STATE;
};
